// Schema types come from the codegenned api.lyse.gen.ts (sourced from
// AERO-Multi-Tenant/contracts/lyse/v1/openapi.yaml). Runtime arrays + the
// query-param shape stay hand-written because they're not in the contract.
//
// SB3-CC-G (lyse/v1@6.0.0 + projects/v1@2.0.0) — vocabulary renames:
//   * InspectionInstructionState: 9 canonical SB3 values (CREATED,
//     SCHEDULED, ONSITE_EXECUTION, DATA_TRANSFER, DATA_PROCESSING,
//     ANALYSIS, COMPLETED, WITHDRAWN, REJECTED). No deprecated aliases.
//   * DataAcquisitionState: 8 single-axis values (CREATED, PROCESSING,
//     PRE_SCREENING, EXPERT_ANALYSIS, EVALUATION_DONE, LOCKED, ISSUE,
//     REJECTED). The analyst sub-workflow inlines into the lifecycle
//     ladder per I5 supersedure.
//   * EvaluationState: RETIRED. The `evaluationState` wire field is
//     dropped from DataAcquisition + DataAcquisitionDetail; the
//     analyst sub-workflow values inline into DataAcquisitionState.
import type { components } from "./api.lyse.gen.js";

// Bound to the contract's components/schemas:
export type DataAcquisition = components["schemas"]["DataAcquisition"];

/**
 * PEDIT-TREE — the data-acquisition list row WITH the additive
 * `inspectionInstructionId` FK that the held BE branch serves, used to link a
 * DA under its inspection instruction in the project entity tree.
 *
 * The base `DataAcquisition` is a codegenned alias (`api.lyse.gen.ts`) and must
 * NOT be hand-edited (CLAUDE.md "Contracts & codegen"). So the small additive
 * FK is overlaid here, in this hand-written companion module, as an
 * intersection — keeping the generated type untouched while the FE can still
 * read the new field. Optional for back-compat with a BE that predates it.
 */
export type DataAcquisitionListItem = DataAcquisition & {
  inspectionInstructionId?: string | null;
};
export type DataAcquisitionDetail =
  components["schemas"]["DataAcquisitionDetail"];
/** @deprecated since lyse contract 1.3.0 — use ImageRef + getInspectionImages. Removed in 2.0.0. */
export type DataAcquisitionImage =
  components["schemas"]["DataAcquisitionImage"];
export type ImageRef = components["schemas"]["ImageRef"];
/**
 * DI-3-5 (lyse/v1@8.6.0) — one flight of the acquisition as the analyst sees
 * it (the intake's `IntakeFlight` minus the pilot-side keys, plus `foreign`).
 * A foreign flight carries no serial, owner, device or firmware.
 */
export type LyseFlight = components["schemas"]["LyseFlight"];
/** DI-3-5 — the detail's non-deleted images per `images.blob_state` (DI-D25). */
export type LyseBlobStateCounts = components["schemas"]["BlobStateCounts"];
/** DI-3-5 — `ImageRef.blobState` (DI-D25 c1's four values). */
export type ImageBlobState = NonNullable<ImageRef["blobState"]>;
export type ImageGeometry = components["schemas"]["ImageGeometry"];
export type ImageUrls = components["schemas"]["ImageUrls"];
export type ImageList = components["schemas"]["ImageList"];
export type InspectionType = components["schemas"]["InspectionType"];
export type InspectionInstructionState =
  components["schemas"]["InspectionInstructionState"];
// SB3 I5 — single-axis DA state (8 values). The analyst sub-workflow
// values inline into this enum.
export type DataAcquisitionState =
  components["schemas"]["DataAcquisitionState"];
export type DataAcquisitionSeverity =
  components["schemas"]["DataAcquisitionSeverity"];
export type GetDataAcquisitionsResponse =
  components["schemas"]["GetDataAcquisitionsResponse"];
export type ExportDataAcquisitionsResponse =
  components["schemas"]["ExportDataAcquisitionsResponse"];
export type ExportFormat = components["schemas"]["ExportFormat"];
export type AssigneeCandidate = components["schemas"]["AssigneeCandidate"];
export type GetAssigneeCandidatesResponse =
  components["schemas"]["GetAssigneeCandidatesResponse"];
export type UpdateAssigneeBody = components["schemas"]["UpdateAssigneeBody"];

/** Story 017 — sortable column keys (server-validated; FE narrows the set). */
export const DATA_ACQUISITION_SORT_KEYS = [
  "instruction_code",
  "instruction_date",
  "planned_date",
  "flight_date",
  "inspection_type",
  "state",
  // SB3-CC-G (lyse/v1@6.0.0): `evaluation_state` sort key retired per
  // I5 supersedure. Sort on `state` covers the full 8-value vocabulary
  // including the analyst sub-workflow values.
  "max_severity",
  // 1.4.0 second pass (2026-05-08): string-name sorts. The BE / dev-api
  // resolves these by enrichment + in-memory sort.
  "asset_identifier",
  "site_name",
  "project_name",
  "pilot_name",
  "turbine_type",
  // 5.4.0 — R58-W-08 (R58-15). Assignee sort with PM-locked
  // "unassigned-first" ordering (direction-stable: unassigned remains
  // first on both asc + desc). BE resolves via the cross-DB enrichment
  // path against `auth_db.users.full_name`.
  "assignee",
] as const;
export type DataAcquisitionSortKey =
  (typeof DATA_ACQUISITION_SORT_KEYS)[number];
export type DataAcquisitionSortOrder = "asc" | "desc";

// Runtime constants — used by the FE filter Autocompletes. Mirror the
// enum values in api.gen.ts (kept in sync because TS would error if a
// member here weren't a member of the generated union).
//
// SB3-CC-G (lyse/v1@6.0.0): 9 canonical SB3 values, no deprecated aliases.
export const INSPECTION_INSTRUCTION_STATES: readonly InspectionInstructionState[] =
  [
    "CREATED",
    "SCHEDULED",
    "ONSITE_EXECUTION",
    "DATA_TRANSFER",
    "DATA_PROCESSING",
    "ANALYSIS",
    "COMPLETED",
    "WITHDRAWN",
    "REJECTED",
  ] as const;

// SB3-CC-G (lyse/v1@6.0.0): single-axis 8-value DataAcquisitionState per
// I5 supersedure. The analyst sub-workflow values (PRE_SCREENING,
// EXPERT_ANALYSIS, EVALUATION_DONE, LOCKED) inline into this enum.
// Drives both the lifecycle and the analyst-workflow Status filter
// chip (the previous separate `EvaluationState` enum + `evaluation_state`
// filter param are retired).
export const DATA_ACQUISITION_STATES: readonly DataAcquisitionState[] = [
  "CREATED",
  "PROCESSING",
  "PRE_SCREENING",
  "EXPERT_ANALYSIS",
  "EVALUATION_DONE",
  "LOCKED",
  "ISSUE",
  "REJECTED",
] as const;

export const INSPECTION_TYPES: readonly InspectionType[] = [
  "routine",
  "post_storm",
  "warranty",
  "commissioning",
] as const;

// Query-param shape for the /data-acquisitions endpoint. NOT in the YAML
// schemas section (only the path-level parameters are), so we keep it
// hand-written. Could be derived from
// paths['/api/lyse/v1/data-acquisitions']['get']['parameters']['query']
// but the indirection is uglier than this. `inspection_type` keeps its
// snake-case wire name because the underlying enum survives the rename.
export interface GetDataAcquisitionsParams {
  page?: number | undefined;
  pageSize?: number | undefined;
  q?: string | undefined;
  // SB3-CC-G (lyse/v1@6.0.0): single-axis 8-value filter per I5
  // supersedure. Covers both the lifecycle values (CREATED, PROCESSING)
  // and the analyst sub-workflow values (PRE_SCREENING, EXPERT_ANALYSIS,
  // EVALUATION_DONE, LOCKED) that used to live on the separate
  // `evaluation_state[]` axis.
  state?: DataAcquisitionState[] | undefined;
  /** SB6-DA-01 (lyse/v1@7.1.0) — "Show completed" toggle. When falsy
   *  (default), the BE hides the terminal states (LOCKED, REJECTED).
   *  When true, every acquisition the caller may read is returned. */
  include_completed?: boolean | undefined;
  inspection_type?: InspectionType | undefined;
  /** Theme-A polish (2026-05-08) — multi-select project filter. UUIDs only. */
  project_id?: string[] | undefined;
  /** Theme-A polish (2026-05-08) — multi-select Park (site) filter. UUIDs only. */
  site_id?: string[] | undefined;
  /** Theme-A polish (2026-05-08) — multi-select assignee filter. UUIDs OR
   *  the literal `__unassigned__` sentinel for the Unassigned pseudo-row. */
  assignee_id?: string[] | undefined;
  date_from?: string | undefined;
  date_to?: string | undefined;
  /** Story 017 — paired arrays of equal length; `sort_by[i]` ordered `sort_order[i]`. */
  sort_by?: DataAcquisitionSortKey[] | undefined;
  sort_order?: DataAcquisitionSortOrder[] | undefined;
  /** TNCY-8 (lyse/v1@8.1.0) — read/list scope. `own` (default, single-schema
   *  fast path) | `member` (cross-tenant fan-out of DAs of granted projects,
   *  DA → inspection_instruction → project chain). Omitted ⇒ BE defaults to
   *  `own`. The FE fires both and merges (`useOwnerMemberDataAcquisitions`,
   *  TNCY-9). */
  scope?: "own" | "member" | undefined;
  /** FCII (lyse/v1@8.3.0) — orphan filter. `unlinked` returns only DAs whose
   *  parent II has no project (`project_id IS NULL` — field-created); `linked`
   *  the complement; omitted / `all` ⇒ no filter. Derived from project_id, not
   *  the II `source`. */
  linkage?: "linked" | "unlinked" | undefined;
}
